import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { messageApi, Message } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { useLanguageStore, t } from '../stores/languageStore';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';
import { zhCN, enUS } from 'date-fns/locale';
import { Bell, Check, Trash2, Mail, CheckCheck } from 'lucide-react';

const MessagesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();
  const { language } = useLanguageStore();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMessages();
  }, [page, isAuthenticated, navigate]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await messageApi.getMessages({ page, pageSize: 20 });
      if (response.success && response.data) {
        setMessages(response.data.messages || []);
        setTotal(response.data.pagination?.total || 0);
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (error) {
      console.error('获取消息失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await messageApi.markAsRead(messageId);
      setMessages(messages.map(msg => 
        msg.id === messageId ? { ...msg, is_read: 1 } : msg
      ));
    } catch (error) {
      toast.error('标记已读失败');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await messageApi.markAllAsRead();
      setMessages(messages.map(msg => ({ ...msg, is_read: 1 })));
      toast.success('已全部标记为已读');
    } catch (error) {
      toast.error('操作失败');
    }
  };

  const handleDelete = async (messageId: number) => {
    try {
      await messageApi.deleteMessage(messageId);
      setMessages(messages.filter(msg => msg.id !== messageId));
      toast.success('删除成功');
    } catch (error) {
      toast.error('删除失败');
    }
  };

  const getMessageTypeLabel = (type: string) => {
    switch (type) {
      case 'system': return { label: t('messages.systemNotice'), color: 'bg-gray-100 text-gray-700' };
      case 'admin': return { label: t('messages.adminNotice'), color: 'bg-blue-100 text-blue-700' };
      case 'like': return { label: t('messages.likeNotice'), color: 'bg-red-100 text-red-700' };
      case 'reply': return { label: t('messages.replyNotice'), color: 'bg-green-100 text-green-700' };
      case 'post_action': return { label: t('messages.postAction'), color: 'bg-purple-100 text-purple-700' };
      default: return { label: t('messages.unknown'), color: 'bg-gray-100 text-gray-700' };
    }
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <Bell className="w-5 h-5 text-primary-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('messages.siteMessages')}</h1>
              <p className="text-sm text-gray-500">{t('messages.totalMessages').replace('{total}', String(total))}</p>
            </div>
          </div>
          {messages.some(msg => msg.is_read === 0) && (
            <button
              onClick={handleMarkAllAsRead}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-50 text-primary-600 rounded-lg hover:bg-primary-100 transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              {t('messages.markAllRead')}
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <Mail className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">{t('messages.noMessages')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {messages.map((message) => {
              const typeInfo = getMessageTypeLabel(message.type);
              return (
                <div
                  key={message.id}
                  className={`p-4 rounded-lg border transition-all ${
                    message.is_read === 0 
                      ? 'bg-blue-50 border-blue-200' 
                      : 'bg-white border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-0.5 text-xs rounded-full ${typeInfo.color}`}>
                          {typeInfo.label}
                        </span>
                        {message.is_read === 0 && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                        )}
                      </div>
                      <h3 className={`font-medium mb-1 ${message.is_read === 0 ? 'text-gray-900' : 'text-gray-700'}`}>
                        {message.title}
                      </h3>
                      <p className="text-sm text-gray-600 mb-2">{message.content}</p>
                      <p className="text-xs text-gray-400">
                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true, locale: language === 'zh' ? zhCN : enUS })}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {message.is_read === 0 && (
                        <button
                          onClick={() => handleMarkAsRead(message.id)}
                          className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                          title={t('messages.markAsRead')}
                        >
                          <Check className="w-4 h-4 text-blue-600" />
                        </button>
                      )}
                      {message.related_id && message.related_type === 'post' && (
                        <button
                          onClick={() => navigate(`/post/${message.related_id}`)}
                          className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                        >
                          {t('messages.viewPost')}
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(message.id)}
                        className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('common.delete')}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-6">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {t('messages.previousPage')}
            </button>
            <span className="text-sm text-gray-600">
              {t('messages.page').replace('{page}', String(page)).replace('{totalPages}', String(totalPages))}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              {t('messages.nextPage')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
